import React, { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Reusable destructive confirmation dialog (red action button). Used wherever
 * we need a "this can't be undone" gate — currently the player-withdraw and
 * admin-delete-competition flows on the Dashboard. Keeps the visual + a11y
 * conventions consistent and lets each call site customise copy + test-ids.
 *
 * `typedConfirmation` (optional): when supplied, the user must type this
 * string exactly before the destructive button unlocks. Added Feb 2026 after
 * a production incident where a player fat-fingered the Withdraw button on
 * mobile and blew through a plain "Are you sure?" dialog by reflex.
 *
 * Explicit testIds (rather than generating them from a prefix) because the
 * existing call sites use slightly inconsistent naming conventions and the
 * already-shipped Playwright regressions assert against those exact strings.
 */
export default function DestructiveConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmingLabel,
  submitting,
  onConfirm,
  dialogTestId,
  cancelTestId,
  confirmTestId,
  typedConfirmation,
}) {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);

  const typedOk = !typedConfirmation || typed.trim().toUpperCase() === typedConfirmation.toUpperCase();
  const disabled = submitting || !typedOk;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(v) => { if (!v && !submitting) onOpenChange(v); }}
    >
      <AlertDialogContent data-testid={dialogTestId} className="bg-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-ink">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-ink-muted">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {typedConfirmation && (
          <div className="mt-2 space-y-2">
            <Label htmlFor="typed-confirm-input" className="text-sm text-ink">
              Type <span className="font-mono font-semibold text-red-700">{typedConfirmation}</span> to confirm
            </Label>
            <Input
              id="typed-confirm-input"
              data-testid="typed-confirm-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoCapitalize="characters"
              placeholder={typedConfirmation}
              disabled={submitting}
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid={cancelTestId}
            disabled={submitting}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid={confirmTestId}
            disabled={disabled}
            onClick={(e) => { e.preventDefault(); if (!disabled) onConfirm(); }}
            className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? confirmingLabel : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
